import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Save, QrCode, ExternalLink, Copy } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader, Modal, Field, Input, Select, ImageUpload } from './AdminUI';

const EMPTY_PAGE = {
  slug: '',
  title: '',
  background_image: '',
  destination_url: '/',
  active: true,
};

const CITY_OPTIONS = [
  { slug: 'desenzano', label: 'Desenzano' },
  { slug: 'sirmione', label: 'Sirmione' },
  { slug: 'peschiera', label: 'Peschiera' },
];

const CATEGORY_OPTIONS = [
  '', 'cosa-vedere', 'ristoranti', 'noleggi', 'sport', 'divertimento',
  'cultura', 'natura', 'trasporti', 'emergenze', 'relax',
];

export default function AdminQrPages() {
  const [pages, setPages] = useState([]);
  const [logos, setLogos] = useState({ desenzanoloft_logo: '', marriott_logo: '' });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  // Destination builder helpers (only relevant while a form is open).
  const [destMode, setDestMode] = useState('builder'); // 'builder' | 'url'
  const [destCity, setDestCity] = useState('desenzano');
  const [destCat, setDestCat] = useState('cosa-vedere');

  const load = async () => {
    setLoading(true);
    try {
      const [pgs, s] = await Promise.all([adminApi.qrPages(), adminApi.getSettings()]);
      setPages(pgs || []);
      setLogos({
        desenzanoloft_logo: s?.desenzanoloft_logo || '',
        marriott_logo: s?.marriott_logo || '',
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ ...EMPTY_PAGE });
    setDestMode('builder');
    setDestCity('desenzano');
    setDestCat('cosa-vedere');
  };

  const openEdit = (p) => {
    setEditing({ ...p });
    // Try to infer builder mode from destination_url
    const url = p.destination_url || '';
    const m = url.match(/^\/(desenzano|sirmione|peschiera)(?:\/([\w-]+))?$/);
    if (m) {
      setDestMode('builder');
      setDestCity(m[1]);
      setDestCat(m[2] || '');
    } else {
      setDestMode('url');
    }
  };

  const applyBuilder = () => {
    const path = destCat ? `/${destCity}/${destCat}` : `/${destCity}`;
    setEditing((prev) => ({ ...prev, destination_url: path }));
  };

  useEffect(() => {
    if (editing && destMode === 'builder') applyBuilder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destMode, destCity, destCat]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await adminApi.updateQrPage(editing.id, editing);
      else await adminApi.createQrPage(editing);
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Errore salvataggio pagina QR');
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminare la pagina /freeversion/${p.slug}?\nATTENZIONE: se il QR code è già stampato smetterà di funzionare.`)) return;
    await adminApi.deleteQrPage(p.id);
    load();
  };

  const copy = (url) => {
    try { navigator.clipboard.writeText(url); } catch {}
  };

  const saveLogo = async (key, value) => {
    setLogos((prev) => ({ ...prev, [key]: value }));
    try {
      await adminApi.setSetting(key, value);
    } catch (e) {
      alert('Errore salvataggio logo: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div>
      <PageHeader
        title="QR Freeversion"
        subtitle="Landing standalone raggiungibili tramite QR code fisici. Sfondo e destinazione configurabili senza toccare il codice."
        action={
          <button
            onClick={openNew}
            className="btn-primary flex items-center gap-2"
            data-testid="qr-new-btn"
          >
            <Plus size={18} /> Nuova pagina
          </button>
        }
      />

      {/* Partner logos (co-brand on /desenzanoloft) */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-8">
        <h3 className="font-semibold text-gray-800 mb-1">Loghi partner per /desenzanoloft</h3>
        <p className="text-xs text-gray-500 mb-4">Caricali qui una sola volta: vengono mostrati automaticamente sopra i badge di download sulla pagina co-brandizzata.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Logo Desenzano Loft" hint="Consigliato PNG trasparente, larghezza min 400px">
            <ImageUpload
              value={logos.desenzanoloft_logo}
              onChange={(url) => saveLogo('desenzanoloft_logo', url)}
            />
          </Field>
          <Field label="Logo Marriott Homes & Villas" hint="Consigliato PNG trasparente, larghezza min 400px">
            <ImageUpload
              value={logos.marriott_logo}
              onChange={(url) => saveLogo('marriott_logo', url)}
            />
          </Field>
        </div>
      </div>

      {/* Pages table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">URL pubblico</th>
              <th className="text-left px-4 py-3">Titolo</th>
              <th className="text-left px-4 py-3">Sfondo</th>
              <th className="text-left px-4 py-3">Destinazione</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="text-right px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Caricamento...</td></tr>
            )}
            {!loading && pages.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nessuna pagina QR configurata.</td></tr>
            )}
            {pages.map((p) => {
              const url = `/freeversion/${p.slug}`;
              return (
                <tr key={p.id} data-testid={`qr-row-${p.slug}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div className="flex items-center gap-2">
                      <QrCode size={14} className="text-gray-400" />
                      <span>{url}</span>
                      <button onClick={() => copy(window.location.origin + url)} className="text-gray-400 hover:text-[color:var(--yup-primary)]" title="Copia URL"><Copy size={13}/></button>
                      <a href={url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-[color:var(--yup-primary)]" title="Apri"><ExternalLink size={13}/></a>
                    </div>
                  </td>
                  <td className="px-4 py-3">{p.title || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3">
                    {p.background_image
                      ? <img src={p.background_image} alt="" className="w-16 h-10 object-cover rounded" />
                      : <span className="text-gray-400 text-xs">(nessuno)</span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-600 break-all max-w-xs">{p.destination_url}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                      {p.active ? 'Attiva' : 'Nascosta'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" data-testid={`qr-edit-${p.slug}`}><Edit2 size={16}/></button>
                      <button onClick={() => del(p)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`qr-delete-${p.slug}`}><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal
        open={!!editing}
        title={editing?.id ? 'Modifica pagina QR' : 'Nuova pagina QR'}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <form onSubmit={save}>
            <Field
              label="Slug (URL: /freeversion/[slug])"
              hint="Solo minuscole, no spazi. ATTENZIONE: cambiare lo slug rende inutilizzabili i QR code già stampati con il valore vecchio."
            >
              <Input
                required
                value={editing.slug}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g,'') })}
                placeholder="areasostacamperlaspiaggia"
                data-testid="qr-form-slug"
              />
            </Field>
            <Field label="Titolo (opzionale, mostrato sotto il logo)">
              <Input
                value={editing.title || ''}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="AREA SOSTA CAMPER LA SPIAGGIA"
                data-testid="qr-form-title"
              />
            </Field>
            <Field label="Immagine di sfondo">
              <ImageUpload
                value={editing.background_image}
                onChange={(url) => setEditing({ ...editing, background_image: url })}
              />
            </Field>

            <Field label="Destinazione del pulsante CLICCA QUI">
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setDestMode('builder')}
                  className={`px-3 py-1.5 text-xs rounded ${destMode === 'builder' ? 'bg-[color:var(--yup-primary)] text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  Città + categoria
                </button>
                <button
                  type="button"
                  onClick={() => setDestMode('url')}
                  className={`px-3 py-1.5 text-xs rounded ${destMode === 'url' ? 'bg-[color:var(--yup-primary)] text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  URL libero
                </button>
              </div>
              {destMode === 'builder' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select value={destCity} onChange={(e) => setDestCity(e.target.value)}>
                    {CITY_OPTIONS.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  </Select>
                  <Select value={destCat} onChange={(e) => setDestCat(e.target.value)}>
                    <option value="">(homepage città)</option>
                    {CATEGORY_OPTIONS.filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </div>
              ) : (
                <Input
                  value={editing.destination_url}
                  onChange={(e) => setEditing({ ...editing, destination_url: e.target.value })}
                  placeholder="/desenzano/cosa-vedere/cultura oppure https://esterno.it"
                  data-testid="qr-form-url"
                />
              )}
              <p className="text-xs text-gray-500 mt-1">
                Anteprima: <span className="font-mono">{editing.destination_url || '/'}</span>
              </p>
            </Field>

            <Field label="Stato">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="qr-form-active"
                />
                <span>Pagina attiva (visibile pubblicamente)</span>
              </label>
            </Field>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Annulla
              </button>
              <button
                type="submit"
                className="btn-primary flex items-center gap-2"
                data-testid="qr-form-save"
              >
                <Save size={16}/> Salva
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
